import { type ImportCustomerContractsSummary } from '@ebalance/proto/bffuser/v1/customer_contract_csv_model_pb'
import { useTranslation } from 'react-i18next'

import { Box, HStack, Text } from '@enechain/ecloud-designsystem'

import { isEnterKey } from '~/libs/keyboard'

/** Step2 サマリカードのクリックフィルタ値。'all' = 処理対象合計 (初期選択)。 */
export type SummaryCardFilter =
  | 'all'
  | 'diff'
  | 'unchanged'
  | 'new'
  | 'changed'
  | 'error'

type Props = {
  summary: ImportCustomerContractsSummary
  activeFilter: SummaryCardFilter
  onFilterChange: (filter: SummaryCardFilter) => void
}

type CardDef = {
  filter: SummaryCardFilter
  label: string
  count: number
  /** 0 件のとき隠す (エラーは 0 件時に出さない: common.md 行レベル仕様)。 */
  hideWhenZero?: boolean
  emphasis?: 'negative'
}

/**
 * Step2 差分プレビューのサマリカード。「据え置き / 新規 / 変更 / エラー / 合計」をクリック式フィルタとして
 * 表示する (顧客契約 CSV は削除を扱わないため 削除 のカードは無い)。
 *
 * 件数はすべて summary (サーバー計算値) から表示する — frontend で行を数え直さない。据え置き行は
 * row_results に含まれない (レスポンスサイズ対策) ため、summary が唯一の SoT。合計は
 * new+changed+unchanged の表示合成で、エラー行は処理対象外なので含めない (common.md「処理対象合計」)。
 * 選択状態は `aria-pressed` で表現し、role="button" の Box に Enter/Space を自前で付ける
 * (`CisContractCsvUpload/components/SummaryCards` と同判断)。
 */
export const SummaryCards = ({
  summary,
  activeFilter,
  onFilterChange,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const cards: CardDef[] = [
    {
      filter: 'unchanged',
      label: t('page:customerContractCsvUpload.step2.summary.unchanged'),
      count: summary.unchangedRowCount,
    },
    {
      filter: 'new',
      label: t('page:customerContractCsvUpload.step2.summary.new'),
      count: summary.newRowCount,
    },
    {
      filter: 'changed',
      label: t('page:customerContractCsvUpload.step2.summary.changed'),
      count: summary.changedRowCount,
    },
    {
      filter: 'error',
      label: t('page:customerContractCsvUpload.step2.summary.error'),
      count: summary.errorRowCount,
      hideWhenZero: true,
      emphasis: 'negative',
    },
    {
      // 差分のみ = 新規 + 変更 (据え置き・エラーは含めない。`CisContractCsvUpload` と同じ。削除は扱わない)
      filter: 'diff',
      label: t('page:customerContractCsvUpload.step2.summary.diff'),
      count: summary.newRowCount + summary.changedRowCount,
    },
    {
      filter: 'all',
      label: t('page:customerContractCsvUpload.step2.summary.total'),
      count:
        summary.newRowCount +
        summary.changedRowCount +
        summary.unchangedRowCount,
    },
  ]

  return (
    <HStack gap={3} marginBottom={3}>
      {cards
        .filter((c) => c.hideWhenZero !== true || c.count > 0)
        .map((card) => (
          <Box
            key={card.filter}
            flex="1"
            padding={3}
            borderWidth="1px"
            borderColor={
              activeFilter === card.filter
                ? 'Border.highEmphasis'
                : 'Border.lowEmphasis'
            }
            borderRadius="md"
            cursor="pointer"
            aria-pressed={activeFilter === card.filter}
            onClick={() => onFilterChange(card.filter)}
            onKeyDown={(e) => {
              if (isEnterKey(e) || e.key === ' ') {
                e.preventDefault()
                onFilterChange(card.filter)
              }
            }}
            role="button"
            tabIndex={0}
          >
            <Text color="Text.midEmphasis" fontSize="sm">
              {card.label}
            </Text>
            <Text
              textStyle="numberStrong"
              color={
                card.emphasis === 'negative' ? 'Text.negativeHigh' : undefined
              }
            >
              {card.count}
            </Text>
          </Box>
        ))}
    </HStack>
  )
}
